import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { PlusIcon, TrashIcon, ArrowPathIcon, ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/20/solid'
import { websiteConfigsApi, type WebsiteConfigInput } from '../../lib/api'
import { useTranslation } from '../../hooks/useTranslation'

function WebsiteConfigForm({
  onSubmit,
  onCancel,
  loading,
  initialData,
}: {
  onSubmit: (data: WebsiteConfigInput) => void
  onCancel: () => void
  loading?: boolean
  initialData?: { name: string; allowedDomains: string[]; isActive: boolean }
}) {
  const [name, setName] = useState(initialData?.name || '')
  const [domains, setDomains] = useState(initialData?.allowedDomains?.join('\n') || '')
  const [isActive, setIsActive] = useState(initialData?.isActive ?? true)
  const { t } = useTranslation()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const allowedDomains = domains
      .split('\n')
      .map(d => d.trim())
      .filter(d => d.length > 0)
    onSubmit({ name, allowedDomains, isActive })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">{t('fields.name')}</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="e.g., B&R Corporate Website"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700">
          {t('pages.websiteConfigs.fields.allowedDomains')}
        </label>
        <textarea
          value={domains}
          onChange={(e) => setDomains(e.target.value)}
          required
          rows={3}
          placeholder="https://example.com&#10;https://www.example.com"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm font-mono"
        />
        <p className="mt-1 text-xs text-gray-500">{t('pages.websiteConfigs.fields.domainsHelp')}</p>
      </div>
      <div className="flex items-center">
        <input
          type="checkbox"
          id="isActive"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
        />
        <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
          {t('pages.websiteConfigs.fields.isActive')}
        </label>
      </div>
      <div className="flex gap-3">
        <button
          type="submit"
          disabled={loading}
          className="flex-1 rounded-md bg-brand-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-500 disabled:opacity-50"
        >
          {loading ? t('common.saving') : t('common.save')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200"
        >
          {t('common.cancel')}
        </button>
      </div>
    </form>
  )
}

function ApiKeyDisplay({ apiKey, onClose }: { apiKey: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false)
  const { t } = useTranslation()

  const handleCopy = () => {
    navigator.clipboard.writeText(apiKey)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {t('pages.websiteConfigs.apiKeyGenerated')}
        </h3>
        <p className="text-sm text-red-600 mb-4">
          {t('pages.websiteConfigs.apiKeyWarning')}
        </p>
        <div className="flex items-center gap-2 bg-gray-100 p-3 rounded-lg">
          <code className="flex-1 text-sm font-mono break-all">{apiKey}</code>
          <button
            onClick={handleCopy}
            className="flex-shrink-0 p-2 text-gray-500 hover:text-gray-700"
          >
            {copied ? (
              <CheckIcon className="h-5 w-5 text-green-500" />
            ) : (
              <ClipboardDocumentIcon className="h-5 w-5" />
            )}
          </button>
        </div>
        <button
          onClick={onClose}
          className="mt-4 w-full rounded-md bg-brand-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-500"
        >
          {t('pages.websiteConfigs.apiKeySaved')}
        </button>
      </div>
    </div>
  )
}

export function WebsiteConfigsPage() {
  const [showForm, setShowForm] = useState(false)
  const [newApiKey, setNewApiKey] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const { data: response, isLoading } = useQuery({
    queryKey: ['website-configs'],
    queryFn: () => websiteConfigsApi.list(),
  })

  const createMutation = useMutation({
    mutationFn: websiteConfigsApi.create,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['website-configs'] })
      setShowForm(false)
      setNewApiKey(data.apiKey)
    },
  })

  const regenerateKeyMutation = useMutation({
    mutationFn: websiteConfigsApi.regenerateKey,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['website-configs'] })
      setNewApiKey(data.apiKey)
    },
  })

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      websiteConfigsApi.update(id, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-configs'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: websiteConfigsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-configs'] })
    },
  })

  if (isLoading) return <div className="p-6">{t('common.loading')}</div>

  const configs = response?.data || []

  return (
    <>
      {newApiKey && (
        <ApiKeyDisplay apiKey={newApiKey} onClose={() => setNewApiKey(null)} />
      )}

      <div className="p-4 xl:p-6">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)]">
          {/* Header */}
          <div className="border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">{t('pages.websiteConfigs.title')}</h1>
                <p className="mt-1 text-sm text-gray-500">{t('pages.websiteConfigs.description')}</p>
              </div>
              <button
                onClick={() => setShowForm(!showForm)}
                className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-500"
              >
                <PlusIcon className="h-4 w-4" />
                {t('pages.websiteConfigs.addButton')}
              </button>
            </div>
          </div>

          {/* Form */}
          {showForm && (
            <div className="border-b border-gray-200 px-6 py-4 bg-gray-50">
              <h3 className="text-sm font-medium text-gray-900 mb-4">{t('pages.websiteConfigs.newTitle')}</h3>
              <WebsiteConfigForm
                onSubmit={(data) => createMutation.mutate(data)}
                onCancel={() => setShowForm(false)}
                loading={createMutation.isPending}
              />
            </div>
          )}

          {/* List */}
          <div className="p-6">
            {configs.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                {t('pages.websiteConfigs.empty')}
              </div>
            ) : (
              <div className="space-y-4">
                {configs.map((config) => (
                  <div
                    key={config.id}
                    className={`border rounded-lg p-4 ${config.isActive ? 'border-gray-200' : 'border-gray-200 bg-gray-50 opacity-60'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-gray-900">{config.name}</h3>
                          {!config.isActive && (
                            <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                              {t('pages.websiteConfigs.inactive')}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          {t('pages.websiteConfigs.fields.apiKeyPrefix')}: <code className="font-mono bg-gray-100 px-1">{config.apiKeyPrefix}...</code>
                        </p>
                        <div className="mt-2">
                          <span className="text-xs text-gray-500">{t('pages.websiteConfigs.fields.allowedDomains')}:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {config.allowedDomains.map((domain, i) => (
                              <span key={i} className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                {domain}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleActiveMutation.mutate({ id: config.id, isActive: !config.isActive })}
                          className={`text-sm px-3 py-1 rounded ${
                            config.isActive
                              ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                        >
                          {config.isActive ? t('pages.websiteConfigs.deactivate') : t('pages.websiteConfigs.activate')}
                        </button>
                        <button
                          onClick={() => {
                            if (confirm(t('pages.websiteConfigs.confirmRegenerate'))) {
                              regenerateKeyMutation.mutate(config.id)
                            }
                          }}
                          className="inline-flex items-center gap-1 text-sm text-amber-600 hover:text-amber-700"
                        >
                          <ArrowPathIcon className="h-4 w-4" />
                          {t('pages.websiteConfigs.regenerateKey')}
                        </button>
                        <button
                          onClick={() => {
                            if (confirm(t('common.confirmDelete'))) {
                              deleteMutation.mutate(config.id)
                            }
                          }}
                          className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700"
                        >
                          <TrashIcon className="h-4 w-4" />
                          {t('common.delete')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
